'use client';

import React, { useEffect, useRef } from 'react';
import { Trophy, RotateCcw, LogIn, Share2 } from 'lucide-react';
import { formatTime, scoreTier } from '@/lib/gameLogic';
import { ORIGIN_BADGE_CLASS, ADVERSARIES } from '@/lib/adversaries';
import Link from 'next/link';

const PENDING_SCORE_KEY = 'adversarymatch_pending_score';

type Props = {
  timeSeconds: number;
  moves: number;
  isLoggedIn: boolean;
  onPlayAgain: () => void;
};

export default function VictoryOverlay({ timeSeconds, moves, isLoggedIn, onPlayAgain }: Props) {
  const tier = scoreTier(timeSeconds, moves);
  const confettiRef = useRef(false);

  useEffect(() => {
    if (confettiRef.current) return;
    confettiRef.current = true;

    // Dynamically import canvas-confetti to avoid SSR issues
    import('canvas-confetti').then((mod) => {
      const confetti = mod.default;
      confetti({
        particleCount: 120,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#E2001A', '#FF4444', '#FF6644', '#FFFFFF', '#FF9999'],
      });
      setTimeout(() => {
        confetti({
          particleCount: 80,
          spread: 55,
          origin: { y: 0.5, x: 0.2 },
          colors: ['#E2001A', '#FF4444', '#FFFFFF'],
        });
        confetti({
          particleCount: 80,
          spread: 55,
          origin: { y: 0.5, x: 0.8 },
          colors: ['#E2001A', '#FF4444', '#FFFFFF'],
        });
      }, 300);
    });
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm fade-in">
      <div className="bg-card border border-border rounded-2xl w-full max-w-sm shadow-2xl victory-entrance">
        {/* Header */}
        <div className="relative p-6 text-center border-b border-border overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-primary/10 to-transparent" />
          <div className="relative">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/30 flex items-center justify-center mx-auto mb-3">
              <Trophy size={28} className="text-primary" />
            </div>
            <h2 className="text-xl font-bold text-foreground">Breach Stopped!</h2>
            <p className="text-sm text-muted-foreground mt-1">All 8 adversary pairs identified</p>
          </div>
        </div>

        {/* Stats */}
        <div className="p-5 space-y-4">
          {/* Tier badge */}
          <div className="flex justify-center">
            <span className={`px-4 py-1.5 rounded-full text-sm font-bold border tracking-widest font-mono ${tier.className}`}>
              {tier.label}
            </span>
          </div>

          {/* Time + moves */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-muted rounded-xl p-4 text-center">
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Time</p>
              <p className="font-mono text-2xl font-bold text-foreground tabular-nums">{formatTime(timeSeconds)}</p>
            </div>
            <div className="bg-muted rounded-xl p-4 text-center">
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Moves</p>
              <p className="font-mono text-2xl font-bold text-foreground tabular-nums">{moves}</p>
            </div>
          </div>

          {/* Adversaries found */}
          <div>
            <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-2">Adversaries Identified</p>
            <div className="flex flex-wrap gap-1.5">
              {ADVERSARIES.map((adv) => (
                <span
                  key={`victory-adv-${adv.id}`}
                  className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${ORIGIN_BADGE_CLASS[adv.origin]}`}
                >
                  {adv.name}
                </span>
              ))}
            </div>
          </div>

          {/* Auth prompt for guests */}
          {!isLoggedIn && (
            <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                <span className="text-primary font-semibold">Score not saved.</span> Sign in to compete on the global leaderboard.
              </p>
            </div>
          )}

          {isLoggedIn && (
            <div className="p-3 rounded-lg bg-success/5 border border-success/20">
              <p className="text-[11px] text-success leading-relaxed font-medium">
                ✓ Score saved to leaderboard
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-5 pt-0 space-y-2">
          <button
            onClick={onPlayAgain}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-accent transition-all duration-150 active:scale-95"
          >
            <RotateCcw size={15} />
            Play Again
          </button>

          {!isLoggedIn && (
            <Link
              href="/sign-up-login-screen"
              onClick={() => {
                if (typeof window !== 'undefined') {
                  localStorage.setItem(
                    PENDING_SCORE_KEY,
                    JSON.stringify({ timeSeconds, moves, savedAt: Date.now() })
                  );
                }
              }}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted transition-all duration-150"
            >
              <LogIn size={15} />
              Sign up to save score
            </Link>
          )}

          {isLoggedIn && (
            <Link
              href="/scoreboard"
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted transition-all duration-150"
            >
              <Share2 size={15} />
              View Leaderboard
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}